import type { LiveRecordingStatus } from "./recording";

export type RecordingUiState = {
  desktopCaptureActive?: boolean;
  liveRecording?: {
    itemId: string;
    status?: LiveRecordingStatus;
    // Stable clock anchors, published on transitions rather than timer ticks.
    recordingStartedAt?: number;
    recordingElapsedMs?: number;
  };
  setLiveRecording: (value: RecordingUiState["liveRecording"]) => void;
};

export type RecordingListener = {
  remove: () => Promise<void>;
};

export type RecordingPorts = {
  openAudioInput?: () => Promise<{ stream: MediaStream; release: () => void }>;
  platform: "android" | "ios" | "web";
  useUiSelector: <Value>(selector: (state: RecordingUiState) => Value) => Value;
  getUiState: () => RecordingUiState;
  useAuthToken: () => string | undefined;
  getAuthToken: () => string | undefined;
  onAppStateChange: (
    listener: (state: { isActive: boolean }) => void,
  ) => Promise<RecordingListener>;
  notifyNow: (notification: {
    id: number;
    title: string;
    body: string;
  }) => Promise<void>;
  reportStopped: (transcriptId: string, finalSequenceNumber: number) => Promise<void>;
  recordStatus: (status: {
    status: string;
    healthStatus?: string;
    pendingChunks?: number;
    pendingBytes?: number;
    isLocalBackupReady?: boolean;
    hasDataLoss?: boolean;
    droppedChunks?: number;
    failedChunks?: number;
    isSilenced?: boolean;
    pausedForSilence?: boolean;
    notificationPermissionGranted?: boolean;
    silenceAlertPosted?: boolean;
  }) => void;
};

let _ports: RecordingPorts | undefined;

export const configureRecordingPorts = (ports: RecordingPorts): void => {
  _ports = ports;
};

const ports = (): RecordingPorts => {
  if (!_ports) {
    throw new Error("Recording ports used before controller construction");
  }
  return _ports;
};

export const getRecordingUiState = (): RecordingUiState => ports().getUiState();

export const openRecordingAudioInput = async (): Promise<{
  stream: MediaStream;
  release: () => void;
}> => {
  const custom = ports().openAudioInput;
  if (custom) return custom();
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  return {
    stream,
    release: () => stream.getTracks().forEach((track) => track.stop()),
  };
};

export const useRecordingUiSelector = <Value>(
  selector: (state: RecordingUiState) => Value,
): Value => ports().useUiSelector(selector);

export const useRecordingAuthToken = (): string | undefined =>
  ports().useAuthToken();

export const getRecordingAuthToken = (): string | undefined =>
  ports().getAuthToken();

export const getRecordingPlatform = (): RecordingPorts["platform"] =>
  ports().platform;

export const onRecordingAppStateChange = (
  listener: (state: { isActive: boolean }) => void,
): Promise<RecordingListener> => ports().onAppStateChange(listener);

export const notifyRecordingNow = (
  notification: Parameters<RecordingPorts["notifyNow"]>[0],
): Promise<void> => ports().notifyNow(notification);

export const reportRecordingStopped = (transcriptId: string, finalSequenceNumber: number): Promise<void> => ports().reportStopped(transcriptId, finalSequenceNumber);

export const recordRecordingStatus = (
  status: Parameters<RecordingPorts["recordStatus"]>[0],
): void => ports().recordStatus(status);
